import { useGetSenatorInstanceQuery } from '__generated__/api-types-and-hooks'
import { readDistrictLogoKey, clearDistrictLogoKey } from 'components/Auth/DistrictSignIn/session'
import { TENANT_LOGO_REGISTRY } from 'components/Auth/DistrictSignIn/logoRegistry'

interface BrandedLogo {
  url: string
  alt: string
}

/**
 * Post-login navbar override: if the user came in via a district-branded
 * sign-in page, the matched senator district was stashed in localStorage.
 * Look up that senator's brand logo (or fall back to a bundled SVG keyed
 * by the senator-instance id) so the navbar can render it instead of the
 * user's own BSO logo.
 */
export const useSenatorBrandedLogo = (): BrandedLogo | null => {
  const districtNumber = readDistrictLogoKey()
  const enabled = Boolean(districtNumber)

  const { data, isLoading, isError } = useGetSenatorInstanceQuery(
    { ilSenateDistrict: districtNumber ?? 0 },
    { enabled, staleTime: 5 * 60 * 1000 }
  )

  if (!enabled) return null
  // Still fetching → don't decide either way (avoids clearing storage on
  // the loading frame, which would race the navbar render and leave it
  // empty even after the query completes).
  if (isLoading) return null

  const instance = data?.getSenatorInstance
  if (!instance) {
    // Query completed (no longer loading) and returned no instance.
    // Drop the stale key only on an explicit null response, not on errors,
    // so transient backend hiccups don't wipe the user's branding.
    if (!isError) clearDistrictLogoKey()
    return null
  }

  const hostedLogo = instance.brandLogoUrl
  const bundled = TENANT_LOGO_REGISTRY[instance.id]?.mainLogo

  const url = hostedLogo || bundled || null
  if (!url) return null

  return {
    url,
    alt: `Senator ${instance.senatorName}`,
  }
}
